// PMA001_UI — "Kontrol et ve eşitle" panel on the Provider Market overview.
// Read-only alignment audit plus operator-confirmed actions that reuse the
// canonical passkey-approved lifecycle endpoints. No direct inference,
// no silent enablement, no fuzzy model matching.
import {useCallback,useEffect,useRef,useState} from 'react';
import type {ReactNode} from 'react';
import {centerApi} from './centerApi';
import type {Data} from './centerApi';
import {providerModelLifecycleApi} from './providerModelLifecycleApi';
import type {BatchResult} from './providerModelLifecycleApi';
import {diagnosisTone} from './diagnosis.mjs';
import {
  providerRows,catalogSyncRows,countsRow,proposals,gatewayUnmatchedRows,
  commercialRow,routesRow,harnessRow,inferenceRow,lastErrorRows,
} from './alignmentState.mjs';
import {useProviderText} from './providerText';

const STATUS_TONE:Record<string,string>={
  connected:'pmc-status connected',ready:'pmc-status ready',
  error:'pmc-status error',off:'pmc-status off',
};

function Chip({tone,value}:{tone?:string;value:ReactNode}){
 return <span className={STATUS_TONE[tone??'off']}>{value}</span>;
}

function CheckRow({tone,title,status,extra}:{tone?:string;title:ReactNode;status:ReactNode;extra?:ReactNode}){
 return <div className="pma-row">
  <strong>{title}</strong>
  <Chip tone={tone} value={status}/>
  {extra?<span className="pma-extra">{extra}</span>:null}
 </div>;
}

type ProposalGroup={provider_key:string;capacity:number;truncated:boolean;upstream_models:Data[]};

type AlignmentPanelProps={
  fresh:boolean;
  onInspectModel:(provider:string,model:string)=>void;
};

function ProposalBlock({data,apply,batchBusy,onInspectModel}:{
  data:Data;apply:(group:ProposalGroup)=>void;batchBusy:string;
  onInspectModel:(provider:string,model:string)=>void;
}){
 const {pt,pr}=useProviderText();
 const groups=proposals(data),gatewayList=gatewayUnmatchedRows(data),errors=lastErrorRows(data);
 if(!groups.length&&!gatewayList.length&&!errors.length)return null;
 return <div className="pma-actions">
  {groups.map(group=><div className="pma-proposal" key={group.provider_key}>
   <div className="pmc-between"><strong>{group.provider_key}</strong><span>{pt(group.capacity)} {pt("model")}</span></div>
   <p className="pmc-muted pma-upstream-list">{group.upstream_models.map(row=>String(row.upstream_model)).join(' · ')}</p>
   <button className="pmc-primary" disabled={!!batchBusy}
     onClick={()=>apply(group)}>{pt("Bu katalog modellerini kaydet (kapalı)")}</button>
  </div>)}
  {gatewayList.map(row=><div className="pma-row" key={'gw:'+row.provider_key+':'+row.model_key}>
   <strong>{pr("{0} · {1}",[String(row.provider_key),String(row.model_key)])}</strong>
   <Chip tone="ready" value={String(row.reason_code)}/>
   <button onClick={()=>onInspectModel(String(row.provider_key),String(row.model_key))}>{pt("Model koşullarını incele")}</button>
  </div>)}
  {errors.map(row=><div className="pma-row" key={'err:'+row.provider_key+':'+row.model_key}>
   <strong>{pr("{0} · {1}",[String(row.provider_key),String(row.model_key)])}</strong>
   <Chip tone={diagnosisTone(row)==='error'?'error':'ready'} value={String(row.error_code)}/>
   <button onClick={()=>onInspectModel(String(row.provider_key),String(row.model_key))}>{pt("Çözümü incele")}</button>
  </div>)}
 </div>;
}

export function AlignmentPanel({fresh,onInspectModel}:AlignmentPanelProps){
 const {pt,pr,time}=useProviderText();
 const [data,setData]=useState<Data|null>(null);
 const [busy,setBusy]=useState(false);
 const [batchBusy,setBatchBusy]=useState('');
 const [error,setError]=useState('');
 const controller=useRef<AbortController|null>(null);
 useEffect(()=>()=>controller.current?.abort(),[]);

 const runCheck=useCallback(async()=>{
  setBusy(true);setError('');
  try{
   const signal=controller.current&&controller.current.signal.aborted===false
     ?controller.current.signal:(controller.current=new AbortController()).signal;
   setData(await centerApi.alignment(signal) as Data);
  }catch(e){setError(e instanceof Error?e.message:'Kontrol tamamlanamadı');}
  finally{setBusy(false);}
 },[]);

 const applyProposals=useCallback(async(group:ProposalGroup)=>{
  setBatchBusy(group.provider_key);setError('');
  try{
   const result:BatchResult=await providerModelLifecycleApi.batch({
    providerKey:group.provider_key,action:'PREPARE_AUTO',acceptSafeProfile:true,
    upstreamModels:group.upstream_models.map(row=>String(row.upstream_model)),
    reason:'PMA001 kontrol-ve-esitle otomatik eslesme, kapali kayit',
   });
   if(result.failed>0)setError('Bazı modeller kaydedilemedi; ayrıntılar model listesinde.');
   const signal=controller.current&&controller.current.signal.aborted===false
     ?controller.current.signal:(controller.current=new AbortController()).signal;
   setData(await centerApi.alignment(signal) as Data);
  }catch(e){setError(e instanceof Error?e.message:'Kayıt tamamlanamadı');}
  finally{setBatchBusy('');}
 },[]);

 const counts=data?countsRow(data):null;
 const routes=data?routesRow(data):null;
 const inference=data?inferenceRow(data):null;
 const commercial=data?commercialRow(data):null;
 const harness=data?harnessRow(data):null;
 const matchTotal=data&&typeof data?.matching?.gateway_total==='number'?data.matching.gateway_total:null;
 const matchCount=data&&typeof data?.matching?.matched_count==='number'?data.matching.matched_count:null;

 return <section className="pma-panel" aria-label={pt("Kontrol et ve eşitle")}>
  <div className="pma-head">
   <div>
    <h2>{pt("Kontrol et ve eşitle")}</h2>
    <p className="pmc-muted">{pt("Bu işlem yalnız salt-okunur denetim yapar; ücretli inference çağrısı ve yetki değişikliği olmadan eşleşme, kota ve rota kanıtlarını tek panelde gösterir.")}</p>
   </div>
   <button className="pmc-primary" disabled={busy} onClick={()=>void runCheck()}>
    {busy?pt("Kontroller çalışıyor…"):pt("Kontrol et ve eşitle")}
   </button>
  </div>

  {data?<p className="pmc-muted pma-observed-time">{pr("Denetim zamanı: {0}",[pt(String(data.observed_at??''))])}</p>:null}
  {error?<p className="pmc-error" role="alert">{pt(error)}</p>:null}
  {batchBusy?<p role="status" className="pmc-pending">{pr("{0} için passkey onaylı kayıt sürüyor…",[batchBusy])}</p>:null}
  {!data&&!busy&&!error?<p className="pmc-empty">{pt("Denetim henüz çalıştırılmadı. Butonu kullanın; hiçbir sağlayıcı çağrısı yapılmaz.")}</p>:null}

  {data?<div className="pma-grid">
   <div className="pma-block">
    <h3>{pt("Sağlayıcı bağlantısı ve Vault")}</h3>
    {providerRows(data).map(row=><CheckRow key={row.provider_key}
     tone={row.tone} title={String(row.display_name||row.provider_key)}
     status={String(row.connection_status)}
     extra={pr("Vault: {0} · Doğrulama: {1}",[
      pt(String(row.vault?.configured===true?'Kayıtlı':'Yok')),
      pt(String(row.vault?.last_verified_status||'Henüz doğrulanmadı'))])}/>)}
   </div>
   <div className="pma-block">
    <h3>{pt("Katalog senkronizasyonu")}</h3>
    {catalogSyncRows(data).map(row=><CheckRow key={row.provider_key}
     tone={String(row.tone)} title={String(row.provider_key)}
     status={String(row.state)} extra={row.error_code?String(row.error_code):undefined}/>)}
    {matchTotal!==null?<CheckRow tone={matchCount===matchTotal?'connected':'ready'}
     title={pt("Katalog/Gateway eşleşmesi")}
     status={pr("{0} / {1} birebir",[pt(matchCount),pt(matchTotal)])}/> :null}
    {counts?<p className="pmc-muted">{pr("Katalog {0} model · Gateway {1} model ({2} etkin)",[pt(counts.catalog_active),pt(counts.gateway_total),pt(counts.gateway_enabled)])}</p>:null}
   </div>
   <div className="pma-block">
    <h3>{pt("Son model inference testi")}</h3>
    {inference?.present?<CheckRow tone={inference.tone}
     title={pr("{0} · {1}",[inference.provider_key,inference.model_key])}
     status={String(inference.status)} extra={time(inference.occurred_at)}/>:
     <p className="pmc-muted">{pt("Kayıt yok.")}</p>}
   </div>
   <div className="pma-block">
    <h3>{pt("Gateway rota uygunluğu")}</h3>
    <CheckRow tone={routes?.tone} title={String(routes?.status||'UNKNOWN')}
     status={String(routes?.status||'UNKNOWN')}
     extra={pr("Seçilebilir rota: {0} · Sağlayıcı: {1} · Dışlanan: {2}",[pt(routes?.selectable_route_count),pt(routes?.selectable_provider_count),pt(routes?.excluded_route_count)])}/>
   </div>
   <div className="pma-block">
    <h3>{pt("Ücretsiz kota / fiyat kanıtı")}</h3>
    <CheckRow tone={commercial?.tone} title={pt("PMA-EVIDENCE")}
     status={commercial?.tone==='connected'?pt("Kota kanıtı ayrılmış"):pt("Kanıtsız model var")}
     extra={pr("FREE kanıtlı: {0} · Ücretli: {1} · Sağlayıcı beyanı: {2} · Kanıtsız: {3}",[pt(commercial?.free),pt(commercial?.paid),pt(commercial?.provider_override),pt(commercial?.missing)])}/>
    <p className="pmc-muted">{pt("Kanıtsız maliyet UNKNOWN kalır; FREE kabul edilmez.")}</p>
   </div>
   <div className="pma-block">
    <h3>{pt("Harness erişimi")}</h3>
    <CheckRow tone={harness?.tone} title={String(harness?.decision||'UNKNOWN')}
     status={String(harness?.decision||'UNKNOWN')}
     extra={harness?.allowed===true?pt("Passkey onaylı kullanım açık"):harness?.allowed===null?pt("Durum bilinmiyor"):pt("Yetki kapalı; passkey ile açın")}/>
   </div>
  </div>:null}

  {data?<ProposalBlock data={data} batchBusy={batchBusy} apply={group=>void applyProposals(group)} onInspectModel={onInspectModel}/>:null}
 </section>;
}
